import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ICONS,
  Button,
  Typography,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useGetDocumentsImporter } from 'src/hooks/document-importer/get-documents-importer.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer';

import { DocumentImporterForm } from './components/document-importer-form';
import { CreateDocumentImporter } from './components/create-document-importer';
import { DocumentImporterTable } from './components/document-importer-table';
import { PreviewDocumentImporter } from './components/preview-document-importer';

import { DOCUMENT_IMPORTER_LIST_MODALS } from './document-importer-list.constants';
import { IDocumentImporterItem } from './document-importer-list.interfaces';
import { HeaderContainer, MainWrapper } from './document-importer-list.styles';

const DocumentImporterList = () => {
  const { t } = useTranslation(['documentImporter', 'common']);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [document, setDocument] = useState<IDocumentImporterItem | null>(null);

  const onModalOpen = () => {
    setActiveModal(DOCUMENT_IMPORTER_LIST_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setDocument(null);
  };

  const isCreate = activeModal === DOCUMENT_IMPORTER_LIST_MODALS.create;

  const currentCompanyId = useGetCurrentCompanyId();

  const { collection, paginationConfig, loading } = useGetDocumentsImporter(currentCompanyId);

  const handleCreate = (values: IDocumentImporterItem) => {
    setDocument(values);
    setActiveModal(null);
  };

  return (
    <>
      <DrawerComponent
        title={t(`documentImporter:labels.startDocumentImport`)}
        onClose={closeModal}
        visible={isCreate}
        destroyOnClose
      >
        <DocumentImporterForm
          onFinishHandler={handleCreate}
          onClose={closeModal}
          currentCompanyId={currentCompanyId}
        />
      </DrawerComponent>
      <MainWrapper>
        {!document && (
          <HeaderContainer>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {t('documentImporter:labels.documentImporter')}
            </Typography>
            <PermissionCheck
              permission={PLATFORM_PERMISSIONS.documentImporter}
              action={PERMISSION_ACTIONS.create}
            >
              <Button
                type="primary"
                ghost
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={onModalOpen}
              >
                {t('documentImporter:buttons.startImport')}
              </Button>
            </PermissionCheck>
          </HeaderContainer>
        )}
        {document ? (
          <Fragment>
            {document?._id ? (
              <PreviewDocumentImporter
                onClose={closeModal}
                documentId={document._id}
                currentCompanyId={currentCompanyId}
              />
            ) : (
              <CreateDocumentImporter
                onClose={closeModal}
                document={document}
                currentCompanyId={currentCompanyId}
              />
            )}
          </Fragment>
        ) : (
          <DocumentImporterTable
            paginationConfig={paginationConfig}
            loading={loading}
            dataSource={collection}
            handleModals={record => {
              setDocument(record);
            }}
          />
        )}
      </MainWrapper>
    </>
  );
};

export default DocumentImporterList;
